import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CLINIC } from "@/lib/brand";
import { KeyRound } from "lucide-react";

/**
 * First-login forced password rotation. Seeded demo accounts land here on their
 * first successful login (when the server returns `must_change_password: true`).
 * After a successful change the auth state is refreshed and the user is sent
 * to their role home.
 */
export default function ForceChangePassword() {
  const { user, refreshMe, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (next === current) { toast.error("New password must be different from the current one"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Welcome aboard!");
      await refreshMe();
      nav(user?.role === "patient" ? "/portal" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[color:var(--bg-marketing)]">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[color:var(--border)] rounded-2xl p-8 shadow-sm"
        data-testid="force-change-password-form"
      >
        <div className="flex items-center gap-3 mb-5">
          <img src={CLINIC.logoUrl} alt={CLINIC.name} className="w-10 h-10 rounded-full ring-1 ring-[color:var(--primary-light)]" />
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--muted)]">First sign-in</p>
            <h1 className="font-heading text-2xl text-[color:var(--ink)] font-medium leading-tight">Set a new password</h1>
          </div>
        </div>

        <p className="text-sm text-[color:var(--muted)] mb-6">
          For security, you must replace the temporary password before continuing.
          You're signed in as <strong className="text-[color:var(--ink)]">{user?.email}</strong>.
        </p>

        <div className="space-y-4">
          <Field label="Current password" value={current} onChange={setCurrent} testid="fcp-current" />
          <Field label="New password" value={next} onChange={setNext} testid="fcp-new" />
          <Field label="Confirm new password" value={confirm} onChange={setConfirm} testid="fcp-confirm" />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full mt-6 rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-11"
          data-testid="fcp-submit"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          {loading ? "Updating…" : "Update password"}
        </Button>

        <button
          type="button"
          onClick={logout}
          className="block mt-4 mx-auto text-xs text-[color:var(--muted)] hover:text-[color:var(--ink)]"
          data-testid="fcp-logout"
        >
          Sign out
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{label}</label>
      <Input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5"
        required
        data-testid={testid}
      />
    </div>
  );
}
